import React, { useEffect, useMemo, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
  STUDENT,
} from '../../util/types';
import {
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  Button,
  LayoutSingleColumn,
  IconReviewStar,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import { ensureUser } from '../../util/data';
import AuthorHeader from '../ListingPage/AuthorHeader';
import EducationAndSpecializationsSection from '../ListingPage/EducationAndSpecializationsSection';
import { levelsList, specializationsList } from '../../util/constants';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import SectionReviews from '../ListingPage/SectionReviews';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
export const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  // Convert money amount to a number and show as credits
  const credits = price ? convertMoneyToNumber(price) : null;
  if (credits == null) return null;

  const creditsNumber = (
    <span className={css.creditNumber}>{credits}</span>
  )

  return (
    <div className={css.price} title={intl.formatMessage({ id: 'ListingCard.credits' }, { credits })}>
      {hasMultiplePriceVariants ? (
        <span className={css.statLabel}>
          <FormattedMessage id="ListingCard.creditsStartingFrom" values={{ credits }} />
        </span>
      ) : (
        <span className={css.creditStatLabel}>
          <FormattedMessage id="ProfilePage.credits" values={{ creditsNumber }} />
        </span>
      )}
    </div>
  );
};
export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage" params={{ tab: 'basic-info' }}>
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage" params={{ tab: 'basic-info' }}>
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeProviderSelected,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
      },
    ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer.length }}
            />
          </Heading>
        ),
        selected: isReviewTypeCustomerSelected,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
      },
    ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  const config = useConfiguration();
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    profileUser
  } = props;
  const history = useHistory();
  // Video controls and overlay
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const overlayHideTimer = useRef(null);

  const clearOverlayTimer = () => {
    if (overlayHideTimer.current) {
      clearTimeout(overlayHideTimer.current);
      overlayHideTimer.current = null;
    }
  };

  const scheduleOverlayHide = (delayMs = 3000) => {
    clearOverlayTimer();
    overlayHideTimer.current = setTimeout(() => setShowOverlay(false), delayMs);
  };
  const handleOverlayToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play();
    } else {
      v.pause();
    }
  };
  const onVideoPlay = () => {
    setIsPlaying(true);
    setShowOverlay(true);
    scheduleOverlayHide(5000);
  };
  const onVideoPause = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };
  const onVideoEnded = () => {
    setIsPlaying(false);
    clearOverlayTimer();
    setShowOverlay(true);
  };

  useEffect(() => () => clearOverlayTimer(), []);

  const nonBlogListings = useMemo(
    () => (Array.isArray(listings) ? listings.filter(l => l?.attributes?.publicData?.listingType !== 'blogs') : []),
    [listings]
  );
  const hasListings = nonBlogListings.length > 0;
  const currentListing = useMemo(() => (hasListings ? nonBlogListings[0] : null), [hasListings, nonBlogListings]);
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const {
    certifications,
    workExperience,
    specializations,
    specializationDescriptions = {},
    videoIntroduction,
    levels,
    sessionTimeMinutes,
    professionalBadge
  } = currentListing?.attributes?.publicData || {};
  const author = currentListing?.author;
  const ensuredAuthor = useMemo(() => ensureUser(profileUser), [profileUser]);

  const variants = ensuredAuthor?.profileImage?.attributes?.variants || {};
  const avatarUrl =
    variants['square-small']?.url ||
    variants['scaled-small']?.url ||
    variants['square-small2x']?.url ||
    variants['listing-card']?.url ||
    variants['listing-card-2x']?.url ||
    null;
  const authorDisplayName = ensuredAuthor?.attributes?.profile?.displayName || null;
  const authorPublicData = ensuredAuthor?.attributes?.profile?.publicData || {};
  const { spokenLanguage, shortTitle, country, education, userType
  } = authorPublicData || {};

  const selectedLevelKey = Array.isArray(levels) ? levels[0] : levels;
  const pageLang = (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const levelItem = useMemo(
    () => levelsList?.find(l => l.value === selectedLevelKey || l.key === selectedLevelKey),
    [selectedLevelKey]
  );
  const level = levelItem ? (pageLang === 'ar' ? levelItem.label_ar : levelItem.label_en) : undefined;
  // Language helper for sections that need localized labels
  const language = useMemo(
    () => (intl?.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en',
    [intl?.locale]
  );

  // Guard: if no listing/user, render minimal content
  if (!hasListings || !currentListing || userType == STUDENT) {
    return (
      <div className={css.modalContent}>
        <AuthorHeader
          avatarUrl={avatarUrl}
          authorDisplayName={authorDisplayName || ''}
          shortTitle={shortTitle}
          spokenLanguage={spokenLanguage}
          bio={bio}
          author={author}
          country={country}
          userType={userType}
          professionalBadge={professionalBadge}
        />
      </div>
    );
  }
  const id = currentListing.id.uuid;
  const { title = '', price } = currentListing.attributes;
  const slug = createSlug(title);
  // Reviews: compute average rating and total count (public, not deleted)
  const reviewList = Array.isArray(reviews) ? reviews : [];
  const validReviews = reviewList.filter(
    r => r?.attributes?.state === 'public' && !r?.attributes?.deleted && typeof r?.attributes?.rating === 'number'
  );
  const reviewCount = validReviews.length;
  const averageRating = reviewCount
    ? validReviews.reduce((sum, r) => sum + (r.attributes.rating || 0), 0) / reviewCount
    : 0;

  return (
    <div className={css.modalContent}>
      <div className={css.previewModalWrapper}>
        <div className={css.leftSide}>
          <AuthorHeader
            avatarUrl={avatarUrl}
            authorDisplayName={authorDisplayName}
            shortTitle={shortTitle}
            spokenLanguage={spokenLanguage}
            bio={bio}
            author={author}
            country={country}
            userType={userType}
            professionalBadge={professionalBadge}
          />
          <EducationAndSpecializationsSection
            certifications={certifications}
            workExperience={workExperience}
            education={education}
            specializations={specializations}
            specializationDescriptions={specializationDescriptions}
            specializationsList={specializationsList}
            language={language}
          />
          <SectionReviews reviews={reviews} fetchReviewsError={queryReviewsError} country={country} />
        </div>
        <div className={css.rightSide}>
          <div className={css.previewRightCard}>
            {videoIntroduction ? (
              <div
                className={css.videoContainer}
                onMouseEnter={() => {
                  setShowOverlay(true);
                  clearOverlayTimer();
                }}
                onMouseLeave={() => {
                  if (isPlaying) scheduleOverlayHide(1500);
                }}
              >
                <video
                  ref={videoRef}
                  className={css.videoElement}
                  src={videoIntroduction}
                  // controls
                  onPlay={onVideoPlay}
                  onPause={onVideoPause}
                  onEnded={onVideoEnded}
                />
                {showOverlay && (
                  <button
                    type="button"
                    className={css.playOverlay}
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    onClick={handleOverlayToggle}
                  >
                    {isPlaying ? (
                      <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="5" y="4" width="5" height="16" fill="currentColor" rx="1" />
                        <rect x="14" y="4" width="5" height="16" fill="currentColor" rx="1" />
                      </svg>
                    ) : (
                      <svg className={css.iconSvg} viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ) : null}
            <div className={css.statRow}>
              <div className={css.statItem}>
                <div className={css.statTitle}>
                  <FormattedMessage id="OrderPanel.levelHeading" defaultMessage="Level" />
                </div>
                <div className={css.statSub}>{level || 'Intermediate'}</div>
              </div>
              <div className={css.statItem}>
                <div className={css.statTitle}>
                  <PriceMaybe
                    price={price}
                    publicData={publicData}
                    config={config}
                    intl={intl}
                  />
                  {/* <span className={css.smallText}>Credits</span> */}
                </div>
                <div className={css.statSub}>
                  <FormattedMessage
                    id="OrderPanel.sessionLength"
                    defaultMessage="{minutes}-min lesson"
                    values={{ minutes: sessionTimeMinutes || 50 }}
                  />
                </div>
              </div>
              <div className={css.statBox}>
                <div className={css.statIconRow}>
                  <IconReviewStar isFilled className={css.star} label="rating" />
                  <span className={css.averageRating}>{averageRating.toFixed(1)}</span>
                </div>
                <div className={css.statValue}>
                  {intl.formatMessage(
                    { id: 'ListingCard.reviewsCount' },
                    { count: reviewCount }
                  )}
                </div>
              </div>

            </div>

            <div className={css.divider} />
            {/* <div className={css.responseTime}>
              <div className={css.responseTimeHeading}>
                <IconCard type="clock" />
                <FormattedMessage id="PreviewSubmitPage.responseTime" defaultMessage="Response time" />
              </div>
              <div className={css.responseTimeValue}>
                {
                  intl.formatMessage({ id: 'PreviewSubmitPage.responseTimeValue' })
                }
              </div>
            </div> */}
            <div className={css.responseTime}>
              <Button type='button' onClick={() => history && slug && id ? history.push(`/l/${slug}/${id}`) : null}>
                {intl.formatMessage({ id: 'ProfilePage.bookTrial' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
      ? currentUser != null || userShowError != null
      : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        // footer={<FooterContainer />}
        className={css.profileWrappers}
      >
        <div className={css.profileWrapper}>
          <MainContent
            bio={bio}
            displayName={displayName}
            userShowError={userShowError}
            publicData={publicData}
            metadata={metadata}
            userFieldConfig={userFields}
            hideReviews={hasNoViewingRightsOnPrivateMarketplace}
            intl={intl}
            userTypeRoles={userTypeRoles}
            profileUser={profileUser}
            {...rest}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
